<script lang="ts" setup>
    import { computed, onMounted, reactive } from 'vue';
    import {PageTitle} from '../../LibMain';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import { PrivilegeType } from "../../Enums/ChoiceEnums";
    import { ActionButtonStyle } from '../../lib/Types/Types'; 
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import { ButtonDisplaySize } from "../../Enums/UiEnums";
    import { ISelectedEstimate, IEstimationConvertToHmtlParams, IEstimationSaveToMmlInputs } from '../../lib/Models/PatientEstimatorModel';
    import { saveEstimation, costEstimator } from '../../lib/ChoicePatientEstimatorApi';
    import { popAlert } from '../../lib/Notification';

    const props = withDefaults(defineProps<{
        screenName: string,
        privilegeLevel: PrivilegeType
    }>(), {
    });

    onMounted(async () => { 
        await getSelectedData();
        await checkStatus();
    });

    const processData = reactive<{
            showModal: boolean,
            selectedEstimateIds: ISelectedEstimate[],
            acceptPostData?: ISelectedEstimate,
            convertToHtmlParams?: IEstimationConvertToHmtlParams,
            pdfReportId?: number,
            estimationSaveToMmlInputs?: IEstimationSaveToMmlInputs,
            patientId?: number
        }>({
            showModal: true,
            selectedEstimateIds: []
        });

    async function getSelectedData() {       
        const selectedList = window.opener.GetSelectionList();
        processData.selectedEstimateIds = selectedList[1].map((item: string | number) => ({estimateId: +item}));

        //noticed sometimes the array could come in with incorrect count and null id values, so handling below
        processData.selectedEstimateIds = processData.selectedEstimateIds.filter(item => item.estimateId != null);  
    }

    async function checkStatus() {
        //determine if estimateId is already accepted or not
        for (const item of processData.selectedEstimateIds) {
            try {
                const resp = await costEstimator.getEstimationById(item.estimateId);

                if (!resp) {
                    popAlert(`There was a problem retrieving estimate ID ${item.estimateId}.`);
                }

                //now update the status in the collection
                updateIsAccepted(item.estimateId, resp.isEstimateAccepted);
            }
            catch(error) {
                console.log('Issue retrieving estimateId', item.estimateId, ' with error: ', error);
            }
        }
    }

    async function updateIsAccepted(estimateId: number, isAcceptedVal: boolean) {
        const item = processData.selectedEstimateIds.find(item => item.estimateId === estimateId);
        if (item) {
            item.isAccepted = isAcceptedVal;
        }
    }

    const selectedEstimateCount = computed<number>(() => {        
        return processData.selectedEstimateIds.length;
    });

    const acceptedEstimates = computed(() => {
        return processData.selectedEstimateIds.filter(item => item.isAccepted);
    });

    const acceptedEstimateCount = computed<number>(() => {        
        return acceptedEstimates.value.length;
    });

    const proposedEstimateCount = computed<number>(() => {        
        return selectedEstimateCount.value - acceptedEstimateCount.value;
    });

    const showSaveButton = computed<boolean>(() => {
        return proposedEstimateCount.value > 0;
    });

    const noProposedSelectedMessage = computed<string>(() => {
        return acceptedEstimateCount.value > 0 && acceptedEstimateCount.value == selectedEstimateCount.value ? `All ${acceptedEstimateCount.value} selected estimates have already been accepted. ` : 
            `You have not selected any estimates to accept.`;
    });

    const prepMessage = computed<string>(() => {
        return acceptedEstimateCount.value > 0 ? `There are ${acceptedEstimateCount.value} estimate(s) out of ${selectedEstimateCount.value} that are 
            already accepted and will not be reprocessed. Are you sure you want to accept the remaining ${proposedEstimateCount.value} proposed estimate(s)?` : 
            `Are you sure you want to accept the ${selectedEstimateCount.value} selected estimates?`;
    });

    const displayMessage = computed<string>(() => {
        return showSaveButton.value ? prepMessage.value : noProposedSelectedMessage.value;
    });

    const hasPrivilege = computed<boolean>(() => props.privilegeLevel >= PrivilegeType.ReadUpdate);
    
    async function acceptEstimates() {
        for (const item of processData.selectedEstimateIds) {
            try {
                if (!item.isAccepted) {
                    processData.acceptPostData = {
                        estimateId: item.estimateId,
                        isAccepted: false
                    }
                    const resp = await saveEstimation.acceptEstimateById(processData.acceptPostData);

                    if (!resp) {
                        popAlert(`There was a problem during acceptance of estimate ID ${item.estimateId}.`);
                    }

                    //now need to process estimate into MML.  Have a few steps for this.
                    //1. Get the printable version and get the pdf_report_id
                    await getPrintableReportForMml(item.estimateId);

                    //2. Get patient id on this estimate, needed to send to MML
                    const result = await costEstimator.getEstimationById(item.estimateId);
                    if (!result) {
                        popAlert(`There was a problem getting the patient ID on estimate ID ${item.estimateId}.`);
                    }
                    processData.patientId = result.patientId;

                    //3. Send the printed report over to MML
                    await SaveAcceptedEstimateToMml(item.estimateId, processData.patientId ?? 0);
                }
            } catch (error) {
                console.log('Acceptance failed for Estimate ID', item.estimateId, ' with error: ', error);
            }
        }

        //refresh the parent screen (TableSearch.htm) and close this popup
        parent.opener.RefreshLastSearch();
        closeWindow();
    }

    async function closeWindow() {
        window.close();
    }

    async function getPrintableReportForMml(estimateId: number) {
        //Get the HTML of the estimate first
        const resp = await costEstimator.getPrintableRazorView(estimateId);

        if (!resp) {
            popAlert(`There was a problem saving the accepted estimate (${estimateId}) to MML.`);
            return;
        }

        processData.convertToHtmlParams = {
            input: "costestimate",
            pdfFileName: "Medical Estimate",
            sectionName: "HTML",
            htmlContent: resp
        }
        const pdfInfo = await costEstimator.convertHtmlToPdf(processData.convertToHtmlParams);

        if (!pdfInfo) {
            popAlert(`We could not retrieve the PDF of this accepted estimate (${estimateId}) to send to MML", "Error Retrieving Estimate PDF during mass acceptance`);
            return;
        }

        //Update the pfd_report_id, then save this to MML
        processData.pdfReportId = pdfInfo.pdf_report_id;        
    }

    async function SaveAcceptedEstimateToMml(estimateId: number, patientId: number) {
        if (processData.pdfReportId != 0) {
            processData.estimationSaveToMmlInputs = {
                estimationId: estimateId,
                pdfReportId: processData.pdfReportId ?? 0,
                patientId: patientId
            }

            return await saveEstimation.saveEstimateToMml(processData.estimationSaveToMmlInputs);
        }
    }

</script>

<template>
    <PageTitle :security-screen-name="props.screenName" >
        <template #permitted>   
            <WindowHeaderFooter :headerHeight="3.5" :footerHeight="5.0">
                <template v-slot:WindowHeader>
                    <div class="isalus-page-title-large pt-3">Accept Selected Estimates</div>
                </template>
                <template v-slot:WindowBody>  
                    <div class="grid-container-process label-isalus-medium">
                        <div>{{ displayMessage }}</div>
                    </div>  
                </template>
                <template v-slot:WindowFooter> 
                    <div class="text-end">
                        <ActionButtonV2 
                            v-if="showSaveButton"
                            :action="ActionButtonStyle.Save"
                            :disabled="!hasPrivilege"
                            :text="'Accept Estimate(s)'"
                            :size="ButtonDisplaySize.Default" 
                            :spinText="'Processing'"
                            :performAction="acceptEstimates"
                        />
                        <ActionButtonV2 
                            v-if="!showSaveButton"
                            :action="ActionButtonStyle.Close"
                            :text="'Close'"
                            :size="ButtonDisplaySize.Default" 
                            :performAction="closeWindow"
                        />
                    </div>
                </template>
            </WindowHeaderFooter> 
        </template>
    </PageTitle>
</template>
